// RoomRenderer — the strategy interface that lets a room render itself as a
// flat equirectangular panorama (Sprint 1 default), a Gaussian splat
// (Sprint 2A scaffold), or a glTF model (Sprint 2B, future). App.ts owns one
// instance of each renderer and dispatches show/hide between them on every
// room or variant change.
//
// Design notes:
// • Renderers are *long-lived* — they're constructed once in App.start() and
//   reused for every show()/hide() cycle. This keeps the panorama sphere
//   alive between visits (texture cache stays warm) and lets the gsplat
//   renderer reuse a loaded asset when the user revisits the same room.
// • show() is async because heavy assets (textures, .ply splats) load on
//   demand. The fade transition in App wraps the call so the user sees a
//   black screen while the new content streams in.
// • setTint composes with the material panel and the SunSystem — both push
//   into the same surface, App multiplies them before applying.

import type { Room, Variant } from '../app/types';
import { resolveRendererKind } from './rendererOverrides';

export interface RoomRenderer {
  /** Activate this renderer for the given room+variant. Resolves when on screen. */
  show(room: Room, variant: Variant): Promise<void>;
  /** Stop rendering — entities disabled, no per-frame cost. Called when switching to a different renderer. */
  hide(): void;
  /** Apply a per-channel tint (1,1,1 = neutral). Material panel + SunSystem both push here. */
  setTint(r: number, g: number, b: number): void;
  /** Restore the neutral 1,1,1 tint. */
  resetTint(): void;
  /** Release GPU/CPU resources permanently. App.destroy() only. */
  destroy(): void;
}

export type RendererKind = 'panorama' | 'gsplat' | 'gltf';

/**
 * Decide which renderer a given room+variant wants. Order of precedence:
 *   1. ?splat=… URL override (operator smoke-test path; see rendererOverrides.ts)
 *   2. variant.renderer
 *   3. room.renderer
 *   4. 'panorama' (default, keeps existing rooms.json configs working)
 */
export function rendererKindFor(room: Room, variant: Variant): RendererKind {
  return resolveRendererKind(room, variant);
}
